import React, { useState, useEffect } from 'react';
import { Form, Modal, Spin } from 'antd';
import moment from 'moment';

import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'app/hooks';
import { selectConfiguration } from 'features/configuration/configurationSlice';
import { selectUser, selectPermissions } from 'features/auth/authSlice';
import { selectEmployee } from 'features/employee/employeeSlice';

import { useDispatch } from 'react-redux';
import { updateEmployee } from 'features/employee/employeeAction';
import { updateUser } from 'features/auth/authSlice';

import { EMPLOYEE_KEY_MENU, INITIAL_EMPLOYEE } from 'features/employee/constants/common';

import SideBarMenu from 'features/employee/components/SideBarMenu';
import FooterButtons from 'features/employee/components/FooterButtons';
import EmployeeInformationFields from './components/EmployeeInformationFields';
import PersonalInformationFields from 'features/employee/components/PersonalInformationFields';
import SkillInformationFields from 'features/employee/components/SkillInformationFields';
// import BonusInformationFields from 'features/employee/components/BonusInformationFields';
// import SalaryInformationFields from 'features/employee/components/SalaryInformationFields';
import RecordHistory from 'features/employee/components/RecordHistory';
import {
  EmployeeModalContent,
  EmployeeModalContentLeft,
  EmployeeModalContentRight,
  EmployeeModalForm,
  EmployeeModal,
  EmployeeAddModalDivider,
  ContainerLoading,
  LoaderItem,
} from './styled';
import NewContractInformationField from 'features/employee/components/NewContractInformationFields/NewContractInformationFields';
import { getUser } from 'utils/auth';
import CertificatesFields from 'features/employee/components/CertificatesFields';

const {
  employeeMenuKey,
  personalMenuKey,
  skillMenuKey,
  historyMenuKey,
  contractMenuKey,
  certificatesMenuKey,
} = EMPLOYEE_KEY_MENU;

interface MyProfileModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const MyProfile = (props: MyProfileModalProps) => {
  const { isVisible, onClose } = props;
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { t } = useTranslation(['validation']);

  const dataConfiguration = useAppSelector(selectConfiguration);
  const dataUser = useAppSelector(selectUser);
  const dataPermissions = useAppSelector(selectPermissions);
  const stateEmployee = useAppSelector(selectEmployee);
  const isLoadingData = stateEmployee.isGettingDetail;

  const [openKeys, setOpenKeys] = useState(employeeMenuKey);
  const [employeeInit, setEmployeeInit] = useState(INITIAL_EMPLOYEE);

  const loadData = () => {
    const data = { ...stateEmployee?.employeeDetail };

    const onBoardStatus =
      dataConfiguration?.dataOnBoardStatus && data?.onBoardStatus
        ? dataConfiguration.dataOnBoardStatus?.find(
            (item: any) => item?.value === data?.onBoardStatus?.id,
          )
        : null;
    const level =
      dataConfiguration?.dataLevel && data?.level
        ? dataConfiguration?.dataLevel?.find((item: any) => item?.value === data?.level?.id)
        : null;
    const contractType =
      dataConfiguration?.dataContractType && data?.contractType
        ? dataConfiguration?.dataContractType?.find(
            (item: any) => item?.value === data?.contractType?.id,
          )
        : null;
    const sex =
      dataConfiguration?.dataSex && data?.sex
        ? dataConfiguration?.dataSex?.find((item: any) => item?.value === data?.sex?.id)
        : null;
    const maritalStatus =
      dataConfiguration?.dataMaritalStatus && data?.maritalStatus
        ? dataConfiguration?.dataMaritalStatus?.find(
            (item: any) => item?.value === data?.maritalStatus?.id,
          )
        : null;
    const religion =
      dataConfiguration?.dataReligion && data?.religion
        ? dataConfiguration?.dataReligion?.find((item: any) => item?.value === data?.religion?.id)
        : null;
    const ethnicity =
      dataConfiguration?.dataEthnicity && data?.ethnicity
        ? dataConfiguration?.dataEthnicity?.find((item: any) => item?.value === data?.ethnicity?.id)
        : null;
    const degree =
      dataConfiguration?.dataDegree && data?.degree
        ? dataConfiguration?.dataDegree?.find((item: any) => item?.value === data?.degree?.id)
        : null;
    const positions =
      data?.positions && data?.positions?.length > 0
        ? data?.positions?.map((item: any) => ({ value: item.id, label: item.name }))
        : [];
    const departments =
      data?.departments && data?.departments?.length > 0
        ? data?.departments?.map((item: any) => ({ value: item.id, label: item.name }))
        : [];
    const managers =
      data?.managers && data?.managers?.length > 0
        ? data?.managers?.map((item: any) => ({
            value: item.employeeId,
            label: `${item.lastName} ${item.firstName}`,
          }))
        : [];
    const roles =
      data?.roles && data?.roles?.length > 0
        ? data?.roles?.map((item: any) => ({ value: item.id, label: item.name }))
        : [];
    const location =
      dataConfiguration?.dataLocation && data?.location
        ? dataConfiguration?.dataLocation?.find((i) => i.value === data?.location?.id)
        : null;

    const projectPrimary = {
      label: data.projectPrimaryName,
      value: data.projectPrimary,
    };
    const projectPrimaryName = data.projectPrimaryName;

    const initValues = data
      ? {
          ...data,
          startDate: data.startDate ? moment(data.startDate) : null,
          endDate: data.endDate ? moment(data.endDate) : null,
          dob: data.dob ? moment(data.dob) : null,
          cidIssuedDate: data.cidIssuedDate ? moment(data.cidIssuedDate) : null,
          contractStartDate: data.contractStartDate ? moment(data.contractStartDate) : null,
          contractExpirationDate: data.contractExpirationDate
            ? moment(data.contractExpirationDate)
            : null,
          trainingPeriodFrom: data.trainingPeriodFrom ? moment(data.trainingPeriodFrom) : null,
          trainingPeriodTo: data.trainingPeriodTo ? moment(data.trainingPeriodTo) : null,

          onBoardStatus: onBoardStatus,
          level: level,
          contractType: contractType,
          sex: sex,
          maritalStatus: maritalStatus,
          religion: religion,
          ethnicity: ethnicity,
          degree: degree,

          positions: positions,
          departments: departments,
          managers: managers,
          roles: roles,
          projectPrimary: projectPrimary,
          projectPrimaryName: projectPrimaryName,
          location: location,
        }
      : INITIAL_EMPLOYEE;

    setEmployeeInit(initValues);
    form.setFieldsValue(initValues);
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateEmployee, dataConfiguration, form]);

  const initDataSubmit = (values: any) => {
    let departmentConvert = null;
    if (Array.isArray(values?.departments)) {
      departmentConvert =
        values?.departments.length > 0
          ? {
              id: values?.departments[0]?.value,
              name: values?.departments[0]?.label,
            }
          : null;
    } else {
      departmentConvert = values?.departments?.value
        ? {
            id: values?.departments?.value,
            name: values?.departments?.label,
          }
        : null;
    }

    let managerConvert = null;
    if (Array.isArray(values?.managers)) {
      managerConvert =
        values?.managers.length > 0
          ? {
              id: values?.managers[0]?.value,
              name: values?.managers[0]?.label,
            }
          : null;
    } else {
      managerConvert = values?.managers?.value
        ? {
            id: values?.managers?.value,
            name: values?.managers?.label,
          }
        : null;
    }

    const tempEmployee = {
      ...stateEmployee?.employeeDetail,
      ...values,
      startDate: values.startDate ? moment(values.startDate).toISOString() : null,
      endDate: values.endDate ? moment(values.endDate).toISOString() : null,
      dob: values.dob ? moment(values.dob).toISOString() : null,
      cidIssuedDate: values.cidIssuedDate ? moment(values.cidIssuedDate).toISOString() : null,
      contractStartDate: values.contractStartDate
        ? moment(values.contractStartDate).toISOString()
        : null,
      contractExpirationDate: values.contractExpirationDate
        ? moment(values.contractExpirationDate).toISOString()
        : null,
      trainingPeriodFrom: values.trainingPeriodFrom
        ? moment(values.trainingPeriodFrom).toISOString()
        : null,
      trainingPeriodTo: values.trainingPeriodTo
        ? moment(values.trainingPeriodTo).toISOString()
        : null,

      onBoardStatusId: values?.onBoardStatus?.value.toString(),
      levelId: values?.level?.value.toString(),
      contractTypeId: values?.contractType?.value.toString(),

      sexId: values?.sex?.value.toString(),
      locationId: values?.location?.value,
      maritalStatusId: values?.maritalStatus?.value.toString(),
      religionId: values?.religion?.value.toString(),
      ethnicityId: values?.ethnicity?.value.toString(),
      degreeId: values?.degree?.value.toString(),
      projectPrimary: values?.projectPrimary?.value,
      projectPrimaryName: values?.projectPrimary?.label,

      departments: departmentConvert ? [departmentConvert] : [],
      managers: managerConvert && managerConvert.id ? [managerConvert] : [],

      skill: employeeInit.skill,
      relative: employeeInit.relative,
    };
    return tempEmployee;
  };

  const handleClickMenu = (keys: any) => {
    setOpenKeys(keys.key);
  };

  const handleResetAll = () => {
    setOpenKeys(employeeMenuKey);
    loadData();
  };

  const handleClose = () => {
    handleResetAll();
    onClose();
  };

  const handleSave = () => {
    form.submit();
  };

  const onFinish = (values: any) => {
    const dataSubmit = initDataSubmit(values);
    dispatch(
      updateEmployee({
        idItem: dataSubmit.id,
        dataRequest: dataSubmit,
        employeeList: stateEmployee.employeeList,
      }),
    );

    const fullName = `${dataSubmit.lastName} ${dataSubmit.firstName}`;
    dispatch(
      updateUser({
        user: {
          ...dataUser,
          fullName: fullName,
        },
      }),
    );
    handleClose();
  };

  const onFinishFailed = (errorInfo: any) => {
    Modal.error({
      title: t('modal:message_invalid'),
    });
  };

  return (
    <>
      <EmployeeModal
        visible={isVisible}
        maskClosable={false}
        onCancel={onClose}
        width={1000}
        style={{ top: 20 }}
        footer={
          !isLoadingData && (
            <FooterButtons
              profileStatus={'None'}
              permissions={dataPermissions}
              handleSave={handleSave}
              handleResetAll={handleResetAll}
              handleSaveAndSendRequestApprove={() => null}
              handleSaveAndApproveProfile={() => null}
              handleSendRequestApprove={() => null}
              handleApproveProfile={() => null}
              handleRejectProfile={() => null}
              handleDeleteProfile={() => null}
            />
          )
        }
      >
        <EmployeeModalContent>
          <EmployeeModalContentLeft>
            <SideBarMenu
              isShowFullMenu={true}
              isShowContract={true}
              employeeData={employeeInit}
              openKeys={openKeys}
              employeeMenuKey={employeeMenuKey}
              personalMenuKey={personalMenuKey}
              skillMenuKey={skillMenuKey}
              historyMenuKey={historyMenuKey}
              certificatesMenuKey={certificatesMenuKey}
              contractMenuKey={contractMenuKey}
              handleClickMenu={handleClickMenu}
              onCloseModal={handleClose}
            />
          </EmployeeModalContentLeft>
          <EmployeeAddModalDivider type="vertical" />
          <EmployeeModalContentRight>
            {isLoadingData ? (
              <ContainerLoading>
                <LoaderItem>
                  <Spin size="large" />
                </LoaderItem>
              </ContainerLoading>
            ) : (
              <EmployeeModalForm
                form={form}
                id="EmployeeForm"
                layout="vertical"
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                initialValues={employeeInit}
              >
                <EmployeeInformationFields
                  isShowComponents={openKeys === employeeMenuKey}
                  isRequriedCheckSave={false}
                  isRequriedCheckSendRequest={true}
                  isRequriedCheckApprove={true}
                  formData={form}
                />
                <PersonalInformationFields
                  isShowComponents={openKeys === personalMenuKey}
                  isMyProfile={true}
                  isRequriedCheckSave={false}
                  isRequriedCheckSendRequest={true}
                  isRequriedCheckApprove={true}
                  employeeData={employeeInit}
                  setEmployeeData={setEmployeeInit}
                  formData={form}
                />
                <SkillInformationFields
                  isShowComponents={openKeys === skillMenuKey}
                  isCanUpdate={true}
                  employeeData={employeeInit}
                  setEmployeeData={setEmployeeInit}
                />
                <CertificatesFields
                  isShowComponents={openKeys === certificatesMenuKey}
                  isCanUpdate={true}
                  employeeData={employeeInit}
                  setEmployeeData={setEmployeeInit}
                />
                {openKeys === contractMenuKey && getUser()?.id && (
                  <NewContractInformationField id={getUser().id} />
                )}
                {openKeys === historyMenuKey && (
                  <RecordHistory itemId={stateEmployee?.employeeDetail.id} />
                )}
              </EmployeeModalForm>
            )}
          </EmployeeModalContentRight>
        </EmployeeModalContent>
      </EmployeeModal>
    </>
  );
};

export default React.memo(MyProfile);
